import { Directive, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { PageAction } from '@progress/kendo-angular-grid/dist/es2015/scrolling/scroller.service';
import _ from 'lodash';
import { BehaviorSubject, Subject, combineLatest, iif, interval } from 'rxjs';
import { bufferTime, distinctUntilChanged, filter, map, share, switchMap, takeUntil, tap } from 'rxjs/operators';

@Directive({
   selector: 'kendo-grid:[samwebVirtualScrollKendoGrid]',
})
export class VirtualScrollKendoGridDirective implements OnInit, OnDestroy, OnChanges {
   private static interval$ = interval(1000).pipe(share());

   private static readonly SKIP = 0;
   private static readonly TAKE = 25;
   // use factor 3 for best results, source: https://www.telerik.com/kendo-angular-ui/components/grid/scroll-modes/virtual/
   private static readonly PAGE_SIZE_FACTOR = 3;
   private static readonly WAS_EXECUTED_DEFAULT = true;
   private static readonly DEFAULT_BUFFER_TIME = 60;
   private static readonly GRID_DATA_EMPTY = <GridDataResult>{ data: [], total: 0 };

   constructor(private kendoGrid: GridComponent) {}

   @Input()
   private virtualScrollData: any[];

   @Input()
   public virtualScrollWasSearchExecuted = VirtualScrollKendoGridDirective.WAS_EXECUTED_DEFAULT;

   @Input()
   public virtualScrollBufferTime = VirtualScrollKendoGridDirective.DEFAULT_BUFFER_TIME;

   @Output()
   public resizingPageComplete = new EventEmitter<boolean>();

   @Output()
   public pageChange = new EventEmitter<PageAction>();

   private pageChange$ = new BehaviorSubject<PageAction>(new PageAction(VirtualScrollKendoGridDirective.SKIP, VirtualScrollKendoGridDirective.TAKE));

   private virtualHeight$ = new BehaviorSubject<number>(0);
   private rowHeight$ = new BehaviorSubject<number>(0);

   private gridData$ = new BehaviorSubject<GridDataResult>(VirtualScrollKendoGridDirective.GRID_DATA_EMPTY);

   private currentSkip$ = new BehaviorSubject<number>(VirtualScrollKendoGridDirective.SKIP);
   private currentTake$ = new BehaviorSubject<number>(VirtualScrollKendoGridDirective.TAKE);

   private virtualScrollData$: BehaviorSubject<any[]>;
   public virtualScrollWasSearchExecuted$: BehaviorSubject<boolean>;

   public virtualScrollBufferTime$: BehaviorSubject<number>;

   private destroy$ = new Subject();

   ngOnInit(): void {
      this.kendoGrid.data = [];
      this.kendoGrid.scrollable = 'virtual';
      this.kendoGrid.skip = VirtualScrollKendoGridDirective.SKIP;
      this.kendoGrid.pageSize = VirtualScrollKendoGridDirective.TAKE;

      this.virtualScrollData$ = new BehaviorSubject<any[]>(this.virtualScrollData ?? []);
      this.virtualScrollWasSearchExecuted$ = new BehaviorSubject<boolean>(
         this.virtualScrollWasSearchExecuted ?? VirtualScrollKendoGridDirective.WAS_EXECUTED_DEFAULT
      );

      this.virtualScrollBufferTime$ = new BehaviorSubject<number>(this.virtualScrollBufferTime);

      // Buffer updates for smoother visuals
      const bufferedDataInserted$ = this.virtualScrollBufferTime$.pipe(
         switchMap((time) =>
            iif(
               () => !_.isNil(time) && time > 0,
               this.gridData$.pipe(
                  bufferTime(this.virtualScrollBufferTime),
                  filter((x) => !_.isEmpty(x)),
                  map((values) => _.last(values))
               ),
               this.gridData$
            )
         )
      );

      // Update skip on page event
      this.kendoGrid.pageChange
         .pipe(
            distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
            map((pageAction) => pageAction.skip),
            filter((skip) => !(_.isNil(skip) || _.isNaN(skip) || !_.isFinite(skip))),
            tap((skip) => this.currentSkip$.next(skip)),
            takeUntil(this.destroy$)
         )
         .subscribe();

      // when search is executed change skip to fire page action
      this.virtualScrollWasSearchExecuted$
         .pipe(
            distinctUntilChanged(),
            filter((virtualScrollWasSearchExecuted) => virtualScrollWasSearchExecuted),
            tap(() => this.currentSkip$.next(VirtualScrollKendoGridDirective.SKIP)),
            takeUntil(this.destroy$)
         )
         .subscribe();

      VirtualScrollKendoGridDirective.interval$
         .pipe(
            tap(() => this.virtualHeight$.next(this.kendoGrid.wrapper.nativeElement.querySelector('.k-virtual-content')?.offsetHeight ?? NaN)),
            tap(() => this.rowHeight$.next(this.kendoGrid.wrapper.nativeElement.querySelector('td')?.offsetHeight ?? NaN)),
            takeUntil(this.destroy$)
         )
         .subscribe();

      // Calc Max count
      combineLatest([this.rowHeight$.pipe(distinctUntilChanged()), this.virtualHeight$.pipe(distinctUntilChanged())])
         .pipe(
            filter(([rowHeight, height]) => !(_.isNil(height) && _.isNaN(height)) && !(_.isNil(rowHeight) && _.isNaN(rowHeight))),
            map(([rowHeight, height]) => height / rowHeight),
            map((calcHeight) => _.round(Math.abs(calcHeight) * VirtualScrollKendoGridDirective.PAGE_SIZE_FACTOR)),
            filter((take) => !(_.isNil(take) || _.isNaN(take) || !_.isFinite(take))),
            filter((take) => take > 0),
            tap((take) => this.currentTake$.next(take)),
            tap(() => this.resizingPageComplete.emit(true)),
            takeUntil(this.destroy$)
         )
         .subscribe();

      // Update Kendo Grid Size, Important otherwise grid will not know how to scroll
      this.virtualHeight$
         .pipe(
            distinctUntilChanged(),
            filter((height) => !(_.isNil(height) && _.isNaN(height))),
            takeUntil(this.destroy$)
         )
         .subscribe((height) => (this.kendoGrid.height = height));

      this.rowHeight$
         .pipe(
            distinctUntilChanged(),
            filter((height) => !(_.isNil(height) && _.isNaN(height))),
            takeUntil(this.destroy$)
         )
         .subscribe((rowHeight) => (this.kendoGrid.rowHeight = rowHeight));

      // Fire Page change when skip is updated
      combineLatest([this.currentSkip$.pipe(distinctUntilChanged()), this.currentTake$.pipe(distinctUntilChanged())])
         .pipe(takeUntil(this.destroy$))
         .subscribe(([skip, take]) => {
            this.kendoGrid.skip = skip;
            this.pageChange$.next(new PageAction(skip, take));
         });

      this.currentTake$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((take) => {
         this.kendoGrid.pageSize = take;
      });

      // Slice Page Change when inserted (triggered through data, search, or scroll)
      this.pageChange$
         .pipe(
            switchMap((pageChange) =>
               this.virtualScrollData$.pipe(
                  filter((data) => !_.isNil(data)),
                  tap(() => this.pageChange.emit(pageChange)),
                  map(
                     (data) =>
                        <GridDataResult>{
                           data: data.slice(pageChange.skip, pageChange.skip + pageChange.take),
                           total: data.length,
                        }
                  )
               )
            ),
            tap((gridData) => this.gridData$.next(gridData)),
            takeUntil(this.destroy$)
         )
         .subscribe();

      // Data should only be set by data result
      bufferedDataInserted$
         .pipe(
            distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
            takeUntil(this.destroy$)
         )
         .subscribe((gridData) => (this.kendoGrid.data = gridData));
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (!_.isNil(changes?.virtualScrollWasSearchExecuted?.currentValue)) {
         if (!_.isNil(this.virtualScrollWasSearchExecuted$)) {
            this.virtualScrollWasSearchExecuted$.next(changes?.virtualScrollWasSearchExecuted?.currentValue);
         } else {
            this.virtualScrollWasSearchExecuted$ = new BehaviorSubject<boolean>(changes?.virtualScrollWasSearchExecuted?.currentValue);
         }
      }

      if (!_.isNil(changes?.virtualScrollBufferTime?.currentValue) && !_.isNaN(changes?.virtualScrollBufferTime?.currentValue)) {
         const newBuffer = changes?.virtualScrollBufferTime?.currentValue;
         if (!_.isNil(this.virtualScrollBufferTime$)) {
            this.virtualScrollBufferTime$.next(newBuffer);
         } else {
            this.virtualScrollBufferTime$ = new BehaviorSubject<number>(newBuffer);
         }
      }

      // undefined values are not allowed on init but on updating the data, seems like Agent List sometimes fires an undefined value after a filter change
      if (!_.isNil(changes.virtualScrollData?.currentValue)) {
         if (!_.isNil(this.virtualScrollData$)) {
            this.virtualScrollData$.next(changes?.virtualScrollData?.currentValue);
         } else {
            this.virtualScrollData$ = new BehaviorSubject<any[]>(changes?.virtualScrollData?.currentValue ?? []);
         }
      }
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
